import React, { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, useNavigate } from "react-router-dom";
import { Toaster } from "react-hot-toast";
import { ClerkProvider, SignedIn, SignedOut, useOrganization, useUser } from "@clerk/clerk-react";
import Dashboard from "./components/Dashboard";
import SignInPage from "./components/SignInPage";
import SignUpPage from "./components/SignUpPage";
import ProtectedRoute from "./components/ProtectedRoute";
import LoadingScreen from "./components/LoadingScreen";
import AuthErrorBoundary from "./components/AuthErrorBoundary";
import { OrganizationProvider } from "./contexts/OrganizationContext";
import CSATSurvey from "./components/CSATSurvey";

import "./App.css";
import { ConfirmDialogProvider } from "./components/ui/Alert";
import OnboardingTour from "./components/OnBoardingTour";

// Separate component to handle tour logic after authentication
function TourWrapper() {
  const [showTour, setShowTour] = useState(false);
  const { user, isLoaded } = useUser();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isLoaded || !user) return;

    // Check if this is a new user or if tour should be shown
    const skipTour = localStorage.getItem('skipOnboardingTour');
    const tourCompleted = localStorage.getItem('onboardingTourCompleted');

    // Show tour for new users (check if user was created in last 5 minutes)
    const userCreatedAt = new Date(user.createdAt);
    const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000);
    const isNewUser = userCreatedAt > fiveMinutesAgo;

    if ((isNewUser || !tourCompleted) && skipTour !== 'true') {
      // Delay to ensure dashboard is fully rendered
      setTimeout(() => setShowTour(true), 1000);
    }
  }, [isLoaded, user]);

  const handleTourComplete = () => {
    setShowTour(false);
    // Optionally navigate to a specific page after tour
    // navigate('/');
  };

  const handleTourSkip = () => {
    setShowTour(false);
  };

  return (
    <OnboardingTour
      show={showTour}
      onComplete={handleTourComplete}
      onSkip={handleTourSkip}
    />
  );
}

function App() {
  const [clerkPubKey, setClerkPubKey] = useState("");
  const isOrgExist = localStorage.getItem('isOrgExist')

  useEffect(() => {
    setClerkPubKey(process.env.REACT_APP_CLERK_PUBLISHABLE_KEY);
  }, []);

  if (!clerkPubKey) {
    return <LoadingScreen message="Initializing application..." />;
  }

  return (
    <AuthErrorBoundary>
      <ClerkProvider
        publishableKey={clerkPubKey}
        appearance={{
          elements: {
            organizationSwitcherTrigger:
              "w-full flex items-center justify-between p-3 text-left bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer",
            organizationPreview: "flex items-center space-x-2",
            organizationSwitcherPopoverCard:
              "bg-white border border-gray-200 rounded-lg shadow-lg",
            organizationSwitcherPopoverFooter: "border-t border-gray-200 p-2",
          },
        }}
        afterSignOutUrl={process.env.REACT_APP_LANDING_URL || "https://uttik-landing-page-prod-325801373008.us-central1.run.app"}
      >
        <ConfirmDialogProvider>
          <BrowserRouter>
            <div className="App">
              <Routes>
                {/* Public authentication routes */}
                <Route
                  path="/sign-in"
                  element={
                    <SignedOut>
                      <SignInPage />
                    </SignedOut>
                  }
                />
                <Route
                  path="/sign-up"
                  element={
                    <SignedOut>
                      <SignUpPage />
                    </SignedOut>
                  }
                />

                {/* Public CSAT Survey route */}
                <Route path="/csat-survey" element={<CSATSurvey />} />

                {/* Protected routes - require authentication */}
                <Route
                  path="/*"
                  element={
                    (() => {
                      const urlParams = new URLSearchParams(window.location.search);
                      const apiKey = urlParams.get('api_key');

                      if (apiKey) {
                        return (
                          <OrganizationProvider>
                            <Dashboard />
                          </OrganizationProvider>
                        );
                      }

                      return (
                        <>
                          <ProtectedRoute>
                            <SignedIn>
                              <OrganizationProvider>
                                <Dashboard />
                                {
                                  // isOrgExist === 'yes' && <TourWrapper />
                                }
                              </OrganizationProvider>
                            </SignedIn>
                          </ProtectedRoute>
                          <SignedOut>
                            {/* You might want to render your LandingPage component here instead of null */}
                            {/* For now, ProtectedRoute handles redirection to Clerk sign-in */}
                            {null}
                          </SignedOut>
                        </>
                      );
                    })()
                  }
                />
              </Routes>
              <Toaster position="top-right" />
            </div>
          </BrowserRouter>
        </ConfirmDialogProvider>
      </ClerkProvider>
    </AuthErrorBoundary>
  );
}

export default App;
